import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * 初期型
 */
export interface ABTestState {
  ruijiRecommendGroupNo: number;
  abTestForManuscriptEGroupNo: number;
  topCounselingTodoListTextAbGroupNo?: string | null;
  jobSearchDetailTargetMemberTagAbGroupNo?: string | null;
  memberRegistrationSuggestionBannerGroupNo?: string | null;
  headerMemberRegistrationButtonGroupNo?: string | null;
  topCounselingAppealModalGroupNo: number;
  appGuidanceModalGroupNo: number;
  careerCounselingReservationGroupNo?: string | null;
  topTodoPreCounselingGroupNo?: string | null;
  matchedTagCassetteAbTestGroupNo?: number | null;
  tManuscriptSummaryAbGroupNo?: string | null;
}

/**
 * Slice型
 */
export interface ABTestSliceState {
  abTest: ReturnType<typeof abTestReducer>;
}

export const ABTestForManuscriptEPattern = {
  Original: 1,
  A: 2,
  B: 3,
  Ineligible: 999,
} as const;

/**
 * 初期状態
 */
export const abTestInitialState: ABTestState = {
  ruijiRecommendGroupNo: 1,
  abTestForManuscriptEGroupNo: ABTestForManuscriptEPattern.Ineligible,
  topCounselingTodoListTextAbGroupNo: '0',
  jobSearchDetailTargetMemberTagAbGroupNo: '0',
  memberRegistrationSuggestionBannerGroupNo: '0',
  headerMemberRegistrationButtonGroupNo: '0',
  topCounselingAppealModalGroupNo: 0,
  appGuidanceModalGroupNo: 1,
  careerCounselingReservationGroupNo: '0',
  topTodoPreCounselingGroupNo: '0',
  matchedTagCassetteAbTestGroupNo: 0,
  tManuscriptSummaryAbGroupNo: '0',
};

/**
 * slice
 */
const abTestSlice = createSlice({
  name: 'abTest',
  initialState: abTestInitialState,
  reducers: {
    setRuijiRecommendGroupNo: (state, action: PayloadAction<number>) => {
      state.ruijiRecommendGroupNo = action.payload;
    },
    setAbTestForManuscriptEGroupNo: (
      state,
      action: PayloadAction<ABTestState['abTestForManuscriptEGroupNo']>
    ) => {
      state.abTestForManuscriptEGroupNo = action.payload;
    },
    setTopCounselingTodoListTextGroupNo: (
      state,
      action: PayloadAction<ABTestState['topCounselingTodoListTextAbGroupNo']>
    ) => {
      state.topCounselingTodoListTextAbGroupNo = action.payload;
    },
    setJobSearchDetailTargetMemberTagAbGroupNo: (
      state,
      action: PayloadAction<
        ABTestState['jobSearchDetailTargetMemberTagAbGroupNo']
      >
    ) => {
      state.jobSearchDetailTargetMemberTagAbGroupNo = action.payload;
    },
    setMemberRegistrationSuggestionBannerGroupNo: (
      state,
      action: PayloadAction<
        ABTestState['memberRegistrationSuggestionBannerGroupNo']
      >
    ) => {
      state.memberRegistrationSuggestionBannerGroupNo = action.payload;
    },
    setHeaderMemberRegistrationButtonGroupNo: (
      state,
      action: PayloadAction<
        ABTestState['headerMemberRegistrationButtonGroupNo']
      >
    ) => {
      state.headerMemberRegistrationButtonGroupNo = action.payload;
    },
    setTopCounselingAppealModalGroupNo: (
      state,
      action: PayloadAction<ABTestState['topCounselingAppealModalGroupNo']>
    ) => {
      state.topCounselingAppealModalGroupNo = action.payload;
    },
    setAppGuidanceModalGroupNo: (
      state,
      action: PayloadAction<ABTestState['appGuidanceModalGroupNo']>
    ) => {
      state.appGuidanceModalGroupNo = action.payload;
    },
    setCareerCounselingReservationGroupNo: (
      state,
      action: PayloadAction<ABTestState['careerCounselingReservationGroupNo']>
    ) => {
      state.careerCounselingReservationGroupNo = action.payload;
    },
    setTopTodoPreCounselingGroupNo: (
      state,
      action: PayloadAction<ABTestState['topTodoPreCounselingGroupNo']>
    ) => {
      state.topTodoPreCounselingGroupNo = action.payload;
    },
    setMatchedTagCassetteAbTestGroupNo: (
      state,
      action: PayloadAction<ABTestState['matchedTagCassetteAbTestGroupNo']>
    ) => {
      state.matchedTagCassetteAbTestGroupNo = action.payload;
    },
    setTManuscriptSummaryAbGroupNo: (
      state,
      action: PayloadAction<ABTestState['tManuscriptSummaryAbGroupNo']>
    ) => {
      state.tManuscriptSummaryAbGroupNo = action.payload;
    },
  },
});

export const {
  setRuijiRecommendGroupNo,
  setAbTestForManuscriptEGroupNo,
  setTopCounselingTodoListTextGroupNo,
  setJobSearchDetailTargetMemberTagAbGroupNo,
  setMemberRegistrationSuggestionBannerGroupNo,
  setHeaderMemberRegistrationButtonGroupNo,
  setTopCounselingAppealModalGroupNo,
  setAppGuidanceModalGroupNo,
  setCareerCounselingReservationGroupNo,
  setTopTodoPreCounselingGroupNo,
  setMatchedTagCassetteAbTestGroupNo,
  setTManuscriptSummaryAbGroupNo,
} = abTestSlice.actions;

export const abTestReducer = abTestSlice.reducer;

type State = ABTestSliceState;

/**
 * Selector ruijiRecommendGroupNoSelector
 * @param state
 * @returns ruijiRecommendGroupNo
 */
export const ruijiRecommendGroupNoSelector = <S extends State>(state: S) =>
  state.abTest.ruijiRecommendGroupNo;

/**
 * Selector abTestForManuscriptEGroupNoSelector
 * @param state
 * @returns abTestForManuscriptEGroupNo
 */
export const abTestForManuscriptEGroupNoSelector = <S extends State>(
  state: S
) => state.abTest.abTestForManuscriptEGroupNo;

/**
 * Selector topCounselingTodoListTextGroupNoSelector
 * @param state
 * @returns topCounselingTodoListTextAbGroupNo
 */
export const topCounselingTodoListTextGroupNoSelector = <S extends State>(
  state: S
) => state.abTest.topCounselingTodoListTextAbGroupNo;

/**
 * Selector jobSearchDetailTargetMemberTagAbGroupNoSelector
 * @param state
 * @returns jobSearchDetailTargetMemberTagAbGroupNo
 */
export const jobSearchDetailTargetMemberTagAbGroupNoSelector = <
  S extends State,
>(
  state: S
) => {
  const { jobSearchDetailTargetMemberTagAbGroupNo } = state.abTest;

  switch (jobSearchDetailTargetMemberTagAbGroupNo) {
    case '1':
      return 'ORIGINAL';
    case '2':
      return 'TEST';
    case '0':
    default:
      return 'N/A';
  }
};

/**
 * Selector memberRegistrationSuggestionBannerGroupNoSelector
 * @param state
 * @returns memberRegistrationSuggestionBannerGroupNo
 */
export const memberRegistrationSuggestionBannerGroupNoSelector = <
  S extends State,
>(
  state: S
) => state.abTest.memberRegistrationSuggestionBannerGroupNo;

/**
 * Selector headerMemberRegistrationButtonGroupNoSelector
 * @param state
 * @returns headerMemberRegistrationButtonGroupNo
 */
export const headerMemberRegistrationButtonGroupNoSelector = <S extends State>(
  state: S
) => state.abTest.headerMemberRegistrationButtonGroupNo;

/**
 * Selector topCounselingAppealModalGroupNoSelector
 * @param state
 * @returns topCounselingAppealModalGroupNo
 */
export const topCounselingAppealModalGroupNoSelector = <S extends State>(
  state: S
) => state.abTest.topCounselingAppealModalGroupNo;

/**
 * Selector appGuidanceModalGroupNoSelector
 * @param state
 * @returns appGuidanceModalGroupNo
 */
export const appGuidanceModalGroupNoSelector = <S extends State>(state: S) =>
  state.abTest.appGuidanceModalGroupNo;

/**
 * Selector careerCounselingReservationGroupNoSelector
 * @param state
 * @returns careerCounselingReservationGroupNo
 */
export const careerCounselingReservationGroupNoSelector = <S extends State>(
  state: S
) => state.abTest.careerCounselingReservationGroupNo;

/**
 * ABテストパターン判定
 * custom_ab_21206: グループ1 (ORIGINAL)
 * custom_ab_21206: グループ2 (TEST)
 */
export const ABTestForCareerCounselingReservation = {
  ORIGINAL: '1',
  TEST: '2',
} as const;

/**
 * Selector topTodoPreCounselingGroupNoSelector
 * @param state
 * @returns topTodoPreCounselingGroupNo
 */
export const topTodoPreCounselingGroupNoSelector = <S extends State>(
  state: S
) => state.abTest.topTodoPreCounselingGroupNo;

/**
 * Selector matchedTagCassetteAbTestGroupNoSelector
 * @param state
 * @returns matchedTagCassetteAbTestGroupNo
 */
export const matchedTagCassetteAbTestGroupNoSelector = <S extends State>(
  state: S
) => state.abTest.matchedTagCassetteAbTestGroupNo;

/**
 * DODASITE-29189 求人詳細_T原稿3点要約表示 ABテストグループ
 */
export type TManuscriptSummaryAbGroup = 'ORIGINAL' | 'TEST' | 'N/A';

/**
 * Selector tManuscriptSummaryAbGroupSelector
 * @param state
 * @returns ABテストグループ（対象外・取得失敗はN/A）
 */
export const tManuscriptSummaryAbGroupSelector = <S extends State>(
  state: S
): TManuscriptSummaryAbGroup => {
  switch (state.abTest.tManuscriptSummaryAbGroupNo) {
    case '1':
      return 'ORIGINAL';
    case '2':
      return 'TEST';
    case '0':
    default:
      return 'N/A';
  }
};
